import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import ConfirmModal from "../../common/ConfirmModal";
import Ipv4TextInput from "../../common/Ipv4TextInput";
import {
  addOrganizationLoginIp,
  fetchClientIpv4,
  fetchOrganizationLoginIps,
  buildAutoAllowlistRuleName,
  buildAllowlistRemoveSuccessMessage,
  isGenericAllowlistApiMessage,
  ipValueFromRow,
  isValidIpv4,
  parseAllowlistEntryToRow,
  removeOrganizationLoginIp,
} from "../../../services/organizationLoginIpService";

const IP_HELP = {
  title: "Networking — IP allowlist",
  points: [
    "Leave the list empty to allow sign-in from any IPv4 network.",
    "Use + Add your client IPv4 to allow your current public IP.",
    "Use + Add allowlist rule to add a new row.",
    "For one IP, use the same Start and End address.",
    "For a range, set Start and End (e.g. 49.0.0.0 to 49.55.255.255).",
    "Rules save when you leave the Start or End IPv4 field (after both are filled).",
  ],
};

const createDraftRow = () => ({
  rowKey: `draft-${Date.now()}-${Math.random()}`,
  serverId: null,
  label: "",
  startIp: "",
  endIp: "",
  savedIpValue: "",
  savedLabel: "",
  isDraft: true,
  isSaving: false,
  error: "",
});

const applyEntriesToRows = (entries, prevRows, { keepRowKeys = [] } = {}) => {
  const saved = (Array.isArray(entries) ? entries : []).map(parseAllowlistEntryToRow);
  const drafts = prevRows.filter(
    (row) => row.isDraft && !keepRowKeys.includes(row.rowKey),
  );
  return [...saved, ...drafts];
};

const getEntriesFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.Entries)) return payload.Entries;
  if (Array.isArray(payload.entries)) return payload.entries;
  return [];
};

const friendlyIpSaveError = (message) => {
  const text = String(message || "").trim();
  if (!text) return "Could not save IP rule.";
  if (text.includes("CIDR") && !text.includes("-")) {
    return `${text} For a start–end range (e.g. 48.43.155.203–48.43.155.205), ensure the API is updated and redeployed.`;
  }
  return text;
};

const normalizeRowIps = (row) => {
  let startIp = String(row.startIp || "").trim();
  let endIp = String(row.endIp || "").trim();
  if (!startIp && endIp && isValidIpv4(endIp)) startIp = endIp;
  if (startIp && !endIp && isValidIpv4(startIp)) endIp = startIp;
  return { startIp, endIp };
};

const OrganizationLoginIpSection = ({
  organizationId,
  staffId,
  showAlert,
  embedded = false,
  isActive = true,
}) => {
  const helpId = useId();
  const [rows, setRows] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);
  const [currentClientIp, setCurrentClientIp] = useState("");
  const [clientIpLoading, setClientIpLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [toolbarBusy, setToolbarBusy] = useState(false);
  const rowsRef = useRef(rows);
  const hasLoadedRef = useRef(false);
  const showAlertRef = useRef(showAlert);
  rowsRef.current = rows;
  showAlertRef.current = showAlert;

  const loadData = useCallback(async () => {
    if (!organizationId) return;
    if (!hasLoadedRef.current) {
      setInitialLoading(true);
    }
    try {
      const response = await fetchOrganizationLoginIps(organizationId);
      if (Number(response?.success) === 1) {
        const entries = getEntriesFromPayload(response.data);
        setRows((prev) => applyEntriesToRows(entries, prev));
        hasLoadedRef.current = true;
      } else {
        showAlertRef.current({
          type: "error",
          message: response?.message || "Could not load IP allowlist.",
        });
      }
    } catch (err) {
      showAlertRef.current({
        type: "error",
        message: err?.message || "Could not load IP allowlist.",
      });
    } finally {
      setInitialLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId || !isActive) return;
    if (!hasLoadedRef.current) {
      loadData();
    }
  }, [organizationId, isActive, loadData]);

  useEffect(() => {
    if (!organizationId) return;
    setClientIpLoading(true);
    fetchClientIpv4()
      .then((ip) => setCurrentClientIp(ip || ""))
      .catch(() => setCurrentClientIp(""))
      .finally(() => setClientIpLoading(false));
  }, [organizationId]);

  const updateRow = (rowKey, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.rowKey === rowKey ? { ...row, ...patch, error: "" } : row)),
    );
  };

  const persistRow = async (rowKey, overrides = {}) => {
    const base = rowsRef.current.find((item) => item.rowKey === rowKey);
    if (!base || base.isSaving) return;

    const row = { ...base, ...overrides };
    const { startIp, endIp } = normalizeRowIps(row);
    const ipValue = ipValueFromRow(startIp, endIp);

    if (!ipValue) {
      const hasAnyInput = Boolean(
        row.label?.trim() || startIp || endIp,
      );
      if (row.isDraft && !hasAnyInput) {
        setRows((prev) => prev.filter((item) => item.rowKey !== rowKey));
      } else if (startIp || endIp) {
        setRows((prev) =>
          prev.map((item) =>
            item.rowKey === rowKey
              ? {
                  ...item,
                  startIp,
                  endIp,
                  error:
                    "Enter valid start and end IPv4 addresses. End must be greater than or equal to start.",
                }
              : item,
          ),
        );
      }
      return;
    }

    const labelTrimmed = row.label.trim();
    if (
      !row.isDraft &&
      ipValue === row.savedIpValue &&
      labelTrimmed === String(row.savedLabel || "").trim()
    ) {
      if (startIp !== row.startIp || endIp !== row.endIp) {
        setRows((prev) =>
          prev.map((item) =>
            item.rowKey === rowKey ? { ...item, startIp, endIp } : item,
          ),
        );
      }
      return;
    }

    const rollback =
      !row.isDraft && row.serverId
        ? {
            ipValue: row.savedIpValue,
            label: row.savedLabel || "",
            serverId: row.serverId,
          }
        : null;

    setRows((prev) =>
      prev.map((item) =>
        item.rowKey === rowKey
          ? { ...item, startIp, endIp, isSaving: true, error: "" }
          : item,
      ),
    );

    try {
      if (!row.isDraft && row.serverId && ipValue !== row.savedIpValue) {
        const removeResponse = await removeOrganizationLoginIp({
          OrganizationId: organizationId,
          Id: row.serverId,
        });
        if (Number(removeResponse?.success) !== 1) {
          throw new Error(removeResponse?.message || "Could not update IP rule.");
        }
      } else if (
        !row.isDraft &&
        row.serverId &&
        ipValue === row.savedIpValue &&
        labelTrimmed !== String(row.savedLabel || "").trim()
      ) {
        const removeResponse = await removeOrganizationLoginIp({
          OrganizationId: organizationId,
          Id: row.serverId,
        });
        if (Number(removeResponse?.success) !== 1) {
          throw new Error(removeResponse?.message || "Could not update IP rule.");
        }
      }

      const addResponse = await addOrganizationLoginIp({
        OrganizationId: organizationId,
        IpValue: ipValue,
        Label: labelTrimmed,
        CreatedBy: staffId || "",
      });

      if (Number(addResponse?.success) !== 1) {
        throw new Error(addResponse?.message || "Could not save IP rule.");
      }

      const entries = getEntriesFromPayload(addResponse.data);
      setRows((prev) => {
        const otherDrafts = prev.filter(
          (item) => item.isDraft && item.rowKey !== rowKey,
        );
        if (entries.length === 0) {
          return prev.map((item) =>
            item.rowKey === rowKey
              ? {
                  ...item,
                  startIp,
                  endIp,
                  label: labelTrimmed,
                  isSaving: false,
                  isDraft: true,
                  serverId: null,
                  savedIpValue: "",
                  savedLabel: "",
                }
              : item,
          );
        }
        return [...entries.map(parseAllowlistEntryToRow), ...otherDrafts];
      });
    } catch (err) {
      const message = friendlyIpSaveError(err?.message);

      if (rollback?.ipValue) {
        try {
          const restoreResponse = await addOrganizationLoginIp({
            OrganizationId: organizationId,
            IpValue: rollback.ipValue,
            Label: rollback.label,
            CreatedBy: staffId || "",
          });
          if (Number(restoreResponse?.success) === 1) {
            const entries = getEntriesFromPayload(restoreResponse.data);
            setRows((prev) => {
              const restored = entries.map(parseAllowlistEntryToRow);
              const otherDrafts = prev.filter(
                (item) => item.isDraft && item.rowKey !== rowKey,
              );
              const editedDraft = {
                ...createDraftRow(),
                label: labelTrimmed,
                startIp,
                endIp,
                error: message,
              };
              return [...restored, editedDraft, ...otherDrafts];
            });
            showAlertRef.current({ type: "error", message });
            return;
          }
        } catch {
          // fall through to keep row locally
        }
      }

      setRows((prev) =>
        prev.map((item) =>
          item.rowKey === rowKey
            ? {
                ...item,
                startIp,
                endIp,
                label: labelTrimmed,
                isSaving: false,
                isDraft: true,
                serverId: null,
                error: message,
              }
            : item,
        ),
      );
      showAlertRef.current({ type: "error", message });
    }
  };

  const handleAddClientIp = async () => {
    if (!currentClientIp || !isValidIpv4(currentClientIp)) {
      showAlertRef.current({
        type: "warning",
        message: "Could not detect a valid IPv4 address for your device.",
      });
      return;
    }

    const duplicate = rowsRef.current.some(
      (row) => ipValueFromRow(row.startIp, row.endIp) === currentClientIp,
    );
    if (duplicate) {
      showAlertRef.current({ type: "warning", message: "Your IP is already in the allowlist." });
      return;
    }

    setToolbarBusy(true);
    try {
      const response = await addOrganizationLoginIp({
        OrganizationId: organizationId,
        IpValue: currentClientIp,
        Label: buildAutoAllowlistRuleName(),
        CreatedBy: staffId || "",
      });
      if (Number(response?.success) === 1) {
        const entries = getEntriesFromPayload(response.data);
        setRows((prev) => applyEntriesToRows(entries, prev));
        showAlertRef.current({ type: "success", message: "Your IP was added." });
      } else {
        showAlertRef.current({
          type: "error",
          message: response?.message || "Could not add your IP.",
        });
      }
    } catch (err) {
      showAlertRef.current({
        type: "error",
        message: err?.message || "Could not add your IP.",
      });
    } finally {
      setToolbarBusy(false);
    }
  };

  const handleAddRule = () => {
    setRows((prev) => [...prev, createDraftRow()]);
  };

  const handleRemove = async (row) => {
    if (row.isDraft) {
      setRows((prev) => prev.filter((item) => item.rowKey !== row.rowKey));
      setConfirmRemove(null);
      return;
    }

    updateRow(row.rowKey, { isSaving: true });
    try {
      const response = await removeOrganizationLoginIp({
        OrganizationId: organizationId,
        Id: row.serverId,
      });
      if (Number(response?.success) === 1) {
        const entries = getEntriesFromPayload(response.data);
        setRows((prev) => applyEntriesToRows(entries, prev));
        const apiMessage = response?.message;
        showAlertRef.current({
          type: "success",
          title: "Rule removed",
          message: isGenericAllowlistApiMessage(apiMessage)
            ? buildAllowlistRemoveSuccessMessage(row)
            : apiMessage,
        });
      } else {
        showAlertRef.current({
          type: "error",
          message: response?.message || "Could not remove IP.",
        });
      }
    } catch (err) {
      showAlertRef.current({
        type: "error",
        message: err?.message || "Could not remove IP.",
      });
    } finally {
      setConfirmRemove(null);
    }
  };

  const handleRowBlur = (rowKey) => {
    const row = rowsRef.current.find((item) => item.rowKey === rowKey);
    if (!row) return;
    if (!row.startIp && !row.endIp && row.isDraft) return;

    const { startIp, endIp } = normalizeRowIps(row);

    if (endIp && !isValidIpv4(endIp)) {
      updateRow(rowKey, { error: "Enter a valid end IPv4 address." });
      return;
    }
    if (startIp && !isValidIpv4(startIp)) {
      updateRow(rowKey, { error: "Enter a valid start IPv4 address." });
      return;
    }

    persistRow(rowKey, { startIp, endIp });
  };

  const toolbarDisabled = toolbarBusy || clientIpLoading;
  const anyRowSaving = rows.some((row) => row.isSaving);

  const renderInfoIcon = () => (
    <div
      className={`org-setup-ip__info${helpOpen ? " is-open" : ""}`}
      onMouseEnter={() => setHelpOpen(true)}
      onMouseLeave={() => setHelpOpen(false)}
    >
      <button
        type="button"
        className="org-setup-ip__info-btn"
        aria-label="How networking IP allowlist works"
        aria-expanded={helpOpen}
        aria-controls={helpId}
        onClick={() => setHelpOpen((open) => !open)}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </button>
      <div id={helpId} className="org-setup-ip__info-popover" role="tooltip">
        <p className="org-setup-ip__info-title">{IP_HELP.title}</p>
        <ul className="org-setup-ip__info-list">
          {IP_HELP.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>
    </div>
  );

  const content = (
    <div className="org-setup-ip">
      <div className="org-setup-ip__header">
        <h6 className="org-setup-ip__title">IP allowlist</h6>
        {renderInfoIcon()}
      </div>

      <div className="org-setup-ip__toolbar">
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          disabled={toolbarDisabled || !currentClientIp}
          onClick={handleAddClientIp}
        >
          <i className="fas fa-plus me-1" aria-hidden="true" />
          Add your client IPv4
          {currentClientIp ? ` (${currentClientIp})` : ""}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={toolbarBusy}
          onClick={handleAddRule}
        >
          <i className="fas fa-plus me-1" aria-hidden="true" />
          Add allowlist rule
        </button>
      </div>

      <div
        className={`org-setup-ip__azure-table-wrap${
          initialLoading ? " org-setup-ip__azure-table-wrap--loading" : ""
        }`}
      >
        {initialLoading ? (
          <p className="text-muted small py-3 px-2 mb-0">Loading IP allowlist...</p>
        ) : null}
        <table className="table org-setup-ip__azure-table mb-0">
          <thead>
            <tr>
              <th>Rule name</th>
              <th>Start IPv4 address</th>
              <th>End IPv4 address</th>
              <th className="org-setup-ip__azure-table-actions" />
            </tr>
          </thead>
          <tbody>
            {!initialLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-muted small py-3">
                  No rules added.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.rowKey} className={row.isSaving ? "org-setup-ip__row--saving" : ""}>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. Office network"
                      value={row.label}
                      disabled={row.isSaving}
                      onChange={(e) => updateRow(row.rowKey, { label: e.target.value })}
                      onBlur={() => {
                        if (!row.isDraft) persistRow(row.rowKey);
                      }}
                    />
                  </td>
                  <td>
                    <Ipv4TextInput
                      value={row.startIp}
                      disabled={row.isSaving}
                      invalid={Boolean(row.error)}
                      onChange={(value) => updateRow(row.rowKey, { startIp: value })}
                      onBlur={() => {
                        const current = rowsRef.current.find(
                          (item) => item.rowKey === row.rowKey,
                        );
                        if (!current) return;
                        if (current.startIp && !isValidIpv4(current.startIp)) {
                          updateRow(row.rowKey, {
                            error: "Enter a valid start IPv4 address.",
                          });
                          return;
                        }
                        handleRowBlur(row.rowKey);
                      }}
                    />
                  </td>
                  <td>
                    <Ipv4TextInput
                      value={row.endIp}
                      disabled={row.isSaving}
                      invalid={Boolean(row.error)}
                      onChange={(value) => updateRow(row.rowKey, { endIp: value })}
                      onBlur={() => handleRowBlur(row.rowKey)}
                    />
                  </td>
                  <td className="org-setup-ip__azure-table-actions text-end">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-primary org-setup-ip__azure-delete"
                      title="Remove rule"
                      disabled={row.isSaving || anyRowSaving}
                      onClick={() => setConfirmRemove(row)}
                    >
                      <i
                        className={`fas ${row.isSaving ? "fa-spinner fa-spin" : "fa-trash-alt"}`}
                        aria-hidden="true"
                      />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {rows.some((row) => row.error) ? (
          <p className="text-danger small mt-2 mb-0 px-2">
            {rows.find((row) => row.error)?.error}
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {embedded ? (
        content
      ) : (
        <div className="card shadow-sm org-setup-saas__panel-card">
          <div className="card-body">{content}</div>
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(confirmRemove)}
        title="Remove rule"
        message={`Remove this allowlist rule${
          confirmRemove?.label ? ` (${confirmRemove.label})` : ""
        }?`}
        confirmText="Remove"
        confirmButtonClass="btn-danger"
        onConfirm={() => handleRemove(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
      />
    </>
  );
};

export default OrganizationLoginIpSection;
